/**
 * InvoiceTemplatesDialog - Galerie de șabloane preset pentru factură.
 * Utilizatorul alege unul din cele 4 modele predefinite ca șablon activ pentru firma sa.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { FileText, Check, Loader2, X, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEMO = {
  supplier_name: "TEHNIC SMART CON S.R.L.",
  supplier_cui: "44303561",
  supplier_reg_com: "J22/1696/2021",
  supplier_address: "Bdul. Socola Nr. 4, Iași",
  supplier_iban: "RO47BTRLRONCRT0463704401",
  supplier_bank: "Banca Transilvania",
  supplier_phone: "0755 898 253",
  supplier_email: "tehnic.smart.con@gmail.com",
  client_name: "Beneficiar Demo S.R.L.",
  client_cui: "39809164",
  client_address: "Str. Demo Nr. 10, Iași",
  invoice_doc_type: "FACTURĂ FISCALĂ",
  invoice_number: "ETS0023",
  invoice_date: "23.02.2026",
  due_date: "23.03.2026",
  items_rows: `<tr><td style="padding:6px; border:1px solid #e2e8f0;">1</td><td style="padding:6px; border:1px solid #e2e8f0;">Servicii izolație termică</td><td style="padding:6px; text-align:center; border:1px solid #e2e8f0;">mp</td><td style="padding:6px; text-align:right; border:1px solid #e2e8f0;">120</td><td style="padding:6px; text-align:right; border:1px solid #e2e8f0;">35.00</td><td style="padding:6px; text-align:right; border:1px solid #e2e8f0;">21%</td><td style="padding:6px; text-align:right; border:1px solid #e2e8f0; font-weight:bold;">4 200.00</td></tr><tr><td style="padding:6px; border:1px solid #e2e8f0;">2</td><td style="padding:6px; border:1px solid #e2e8f0;">Vopsitorie interior 2 straturi</td><td style="padding:6px; text-align:center; border:1px solid #e2e8f0;">mp</td><td style="padding:6px; text-align:right; border:1px solid #e2e8f0;">85</td><td style="padding:6px; text-align:right; border:1px solid #e2e8f0;">18.00</td><td style="padding:6px; text-align:right; border:1px solid #e2e8f0;">21%</td><td style="padding:6px; text-align:right; border:1px solid #e2e8f0; font-weight:bold;">1 530.00</td></tr>`,
  subtotal: "5 730.00",
  vat_amount: "1 203.30",
  total: "6 933.30",
  notes: "Plata se va efectua prin transfer bancar în 30 zile. (Deviz nr. 1234 · Contract nr. 5)",
  payment_status: "DE ACHITAT",
};

const renderTemplate = (html, overrides = {}) => {
  const data = { ...DEMO, ...overrides };
  let out = html || "";
  Object.entries(data).forEach(([k, v]) => {
    out = out.split(`{{${k}}}`).join(v ?? "");
  });
  return out;
};

export default function InvoiceTemplatesDialog({ open, onOpenChange, companyId, companyLogoUrl }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [preview, setPreview] = useState(null);

  const fetchPresets = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/invoice-templates`, { params: { company_id: companyId } });
      setPresets(r.data || []);
    } catch {
      toast.error("Eroare la încărcarea șabloanelor");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (open) fetchPresets();
  }, [open, fetchPresets]);

  const selectPreset = async (preset) => {
    if (preset.is_default) return;
    setSelecting(preset.preset_id);
    try {
      await axios.post(`${API}/invoice-templates/select`, { preset_id: preset.preset_id }, { params: { company_id: companyId } });
      toast.success(`Șablon activ: ${preset.name}`);
      fetchPresets();
    } catch {
      toast.error("Eroare la activarea șablonului");
    } finally {
      setSelecting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[96vw] max-h-[92vh] overflow-hidden p-0 flex flex-col" data-testid="invoice-templates-dialog">
        <DialogHeader className="p-4 sm:p-5 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-purple-600" />
            Alege formatul facturii
          </DialogTitle>
          <DialogDescription className="text-xs">
            Alege unul din cele 4 modele predefinite. Modelul selectat se folosește la generarea PDF-urilor de factură.
            Logo-ul firmei se preia automat din Setări.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 p-4 sm:p-5">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {presets.map((p) => {
                const mini = renderTemplate(p.html_content, { logo_url: companyLogoUrl || "" });
                return (
                  <div
                    key={p.preset_id}
                    className={`relative rounded-xl border-2 transition-all overflow-hidden ${p.is_default ? "border-purple-500 shadow-lg shadow-purple-500/20" : "border-slate-200 hover:border-purple-300"}`}
                    data-testid={`invoice-preset-card-${p.preset_id}`}
                  >
                    {p.is_default && (
                      <Badge className="absolute top-2 right-2 bg-purple-600 text-white z-10">
                        <Check className="w-3 h-3 mr-1" /> ACTIV
                      </Badge>
                    )}
                    <div className="aspect-[3/4] bg-slate-50 overflow-hidden relative cursor-pointer" onClick={() => setPreview(p)}>
                      <div
                        className="absolute inset-0 origin-top-left pointer-events-none"
                        style={{ transform: "scale(0.42)", transformOrigin: "top left", width: "238%", height: "238%" }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mini, { USE_PROFILES: { html: true } }) }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
                    </div>
                    <div className="p-3 border-t bg-white">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="font-semibold text-sm flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: p.accent }} />
                          {p.name}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setPreview(p)} className="h-7 text-xs" data-testid={`invoice-preset-preview-${p.preset_id}`}>
                          <Eye className="w-3 h-3 mr-1" /> Mărește
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-500 mb-2 line-clamp-2">{p.description}</p>
                      {p.is_default ? (
                        <Button size="sm" disabled className="w-full bg-purple-600 hover:bg-purple-700 text-white h-8 text-xs cursor-default">
                          <Check className="w-3.5 h-3.5 mr-1" /> Șablon activ
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => selectPreset(p)}
                          disabled={selecting === p.preset_id}
                          className="w-full bg-purple-600 hover:bg-purple-700 h-8 text-xs"
                          data-testid={`invoice-preset-activate-${p.preset_id}`}
                        >
                          {selecting === p.preset_id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : null}
                          Activează acest model
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="border-t p-3 flex justify-end shrink-0 bg-slate-50">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-1" /> Închide
          </Button>
        </div>

        {/* Preview Dialog mare */}
        {preview && (
          <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
            <DialogContent className="max-w-4xl w-[96vw] max-h-[92vh] overflow-hidden p-3 flex flex-col">
              <DialogHeader className="shrink-0">
                <DialogTitle>Previzualizare: {preview.name}</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-auto bg-slate-100 rounded p-3">
                <div
                  className="bg-white mx-auto shadow"
                  style={{ maxWidth: 780 }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderTemplate(preview.html_content, { logo_url: companyLogoUrl || "" }), { USE_PROFILES: { html: true } }) }}
                  data-testid="invoice-preset-fullpreview"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 shrink-0 border-t mt-2">
                {!preview.is_default && (
                  <Button onClick={() => { selectPreset(preview); setPreview(null); }} className="bg-purple-600 hover:bg-purple-700">
                    Activează acest model
                  </Button>
                )}
                <Button variant="outline" onClick={() => setPreview(null)}>Închide</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
